import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Search,
    Filter,
    X,
    ChevronDown,
    Calendar,
    Download,
    FileSpreadsheet,
    RotateCcw,
    Check,
    Archive,
    Building2,
    Briefcase,
    Users,
    UserRound,
    Tag
} from 'lucide-react';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import api from '../../../api/client';
import PremiumSelect from '../component/PremiumSelect';
import PremiumDatePicker from '../component/PremiumDatePicker';
import { getTagColor } from '../../../utils/tagColors';

const PRESET_TAGS = ['Priority', 'Re-interview', 'Campus', 'Referral'];

const STATUS_TAB_THEME = {
    '': {
        idle: 'text-slate-700 bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300',
        active: 'bg-slate-800 text-white border-slate-800 shadow-sm',
        badge: 'bg-slate-700 text-white',
        badgeActive: 'bg-white/20 text-white',
        dot: 'bg-slate-400',
    },
    pending: {
        idle: 'text-amber-900 bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300',
        active: 'bg-amber-600 text-white border-amber-600 shadow-sm',
        badge: 'bg-amber-600 text-white',
        badgeActive: 'bg-white/25 text-white',
        dot: 'bg-amber-500',
    },
    completed: {
        idle: 'text-emerald-900 bg-emerald-50 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300',
        active: 'bg-emerald-600 text-white border-emerald-600 shadow-sm',
        badge: 'bg-emerald-600 text-white',
        badgeActive: 'bg-white/25 text-white',
        dot: 'bg-emerald-500',
    },
    expired: {
        idle: 'text-rose-900 bg-rose-50 border-rose-200 hover:bg-rose-100 hover:border-rose-300',
        active: 'bg-rose-600 text-white border-rose-600 shadow-sm',
        badge: 'bg-rose-600 text-white',
        badgeActive: 'bg-white/25 text-white',
        dot: 'bg-rose-500',
    },
};

const InterviewFilters = ({
    filters,
    statusCounts,
    candidates = [],
    onFilterChange,
    onClearFilters,
    hasActiveFilters,
    showAdvancedFilters,
    setShowAdvancedFilters,
    companies = [],
    departments = [],
    companyAdmins = [],
    jobDescriptions = [],
    statusOptions = [],
    onExport,
    onExportCSV,
    isExporting = false,
    totalCount = 0,
    selectedCount = 0
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = document.documentElement.dir === 'rtl' || i18n?.language === 'ar';
    const searchInputRef = useRef(null);
    const drawerRef = useRef(null);

    const [availableTags, setAvailableTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [exportedCSV, setExportedCSV] = useState(false);

    // Today's local date string (YYYY-MM-DD)
    const todayStr = useMemo(() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }, []);

    // Fetch available tags from backend
    const fetchAvailableTags = useCallback(async () => {
        try {
            setTagsLoading(true);
            const response = await api.get('tags/', {
                headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` }
            });
            const tagsList = response.data?.results || response.data || [];
            setAvailableTags(tagsList);
        } catch (err) {
            console.error('Error fetching available tags:', err);
        } finally {
            setTagsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAvailableTags();
    }, [fetchAvailableTags]);

    // Sync selected tags from filters string
    useEffect(() => {
        if (filters.tags) {
            const tagsArray = filters.tags.split(',').map(t => t.trim()).filter(Boolean);
            setSelectedTags(tagsArray);
        } else {
            setSelectedTags([]);
        }
    }, [filters.tags]);

    // Keyboard shortcut: '/' focuses the search input
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && showAdvancedFilters) {
                setShowAdvancedFilters(false);
                return;
            }
            if (e.key === '/' && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showAdvancedFilters, setShowAdvancedFilters]);

    // Lock body scroll when drawer is open
    useEffect(() => {
        if (showAdvancedFilters) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showAdvancedFilters]);

    const handleTagToggle = (tagName) => {
        let newTags;
        if (selectedTags.includes(tagName)) {
            newTags = selectedTags.filter(t => t !== tagName);
        } else {
            newTags = [...selectedTags, tagName];
        }
        setSelectedTags(newTags);
        onFilterChange('tags', newTags.join(','));
    };

    const handleExportCSVClick = () => {
        if (onExportCSV) {
            onExportCSV();
            setExportedCSV(true);
            setTimeout(() => setExportedCSV(false), 2500);
        }
    };

    const handleClearAdvancedFilters = (e) => {
        e?.stopPropagation();
        if (typeof onFilterChange === 'function') {
            onFilterChange({
                company_id: '',
                department_id: '',
                scheduled_by: '',
                job_description_id: '',
                filter_date_from: '',
                filter_date_to: '',
                tags: '',
                show_archived: ''
            });
        }
        setSelectedTags([]);
    };

    // Merged tags: preset list + any extra from DB
    const allDisplayTags = useMemo(() => {
        const dbTags = availableTags.map(tag => (typeof tag === 'string' ? tag : tag.name)).filter(Boolean);
        return Array.from(new Set([...PRESET_TAGS, ...dbTags]));
    }, [availableTags]);

    // Count of active advanced filters (excluding search & status)
    const activeAdvancedFilterCount = useMemo(() => {
        return [
            filters.company_id,
            filters.department_id,
            filters.scheduled_by,
            filters.job_description_id,
            filters.filter_date_from,
            filters.filter_date_to,
            filters.tags,
            filters.show_archived === 'true'
        ].filter(Boolean).length;
    }, [filters]);

    // Status tabs definition matching Image 1
    const statusTabs = useMemo(() => {
        const allCount = statusCounts?.all ?? totalCount;
        const pendingCount = statusCounts?.pending ?? (candidates?.filter(c => c.status === 'pending').length || 0);
        const completedCount = statusCounts?.completed ?? (candidates?.filter(c => c.status === 'completed').length || 0);
        const expiredCount = statusCounts?.expired ?? (candidates?.filter(c => c.status === 'expired').length || 0);

        return [
            { value: '', label: 'All', count: allCount },
            { value: 'pending', label: 'Scheduled', count: pendingCount },
            { value: 'completed', label: 'Completed', count: completedCount },
            { value: 'expired', label: 'Expired', count: expiredCount },
        ];
    }, [statusCounts, totalCount, candidates]);

    // Dropdown options memoized for PremiumSelect
    const companyOptions = useMemo(() => [
        { value: '', label: t('interviewOverview.allCompanies', 'All companies') },
        ...companies.map((c) => ({
            value: String(c.id),
            label: c.name || String(c.id)
        }))
    ], [companies, t]);

    const departmentOptions = useMemo(() => [
        { value: '', label: t('interviewOverview.allDepartments', 'All departments') },
        ...departments.map((d) => ({
            value: String(d.id),
            label: d.name || String(d.id)
        }))
    ], [departments, t]);

    const companyAdminOptions = useMemo(() => [
        { value: '', label: t('interviewOverview.allCompanyAdmins', 'All company admins') },
        ...companyAdmins.map((admin) => ({
            value: String(admin.id),
            label: admin.name || String(admin.id)
        }))
    ], [companyAdmins, t]);

    const positionOptions = useMemo(() => [
        { value: '', label: t('interviewOverview.allPositions', 'All positions') },
        ...jobDescriptions.map((jd) => ({
            value: String(jd.id),
            label: jd.title || String(jd.id)
        }))
    ], [jobDescriptions, t]);

    return (
        <div className="bg-white border-b border-gray-100 px-4 sm:px-6 py-2.5 shrink-0">
            {/* Single Row: Search | centered status tabs | Export CSV + Filters */}
            <div className="grid grid-cols-[minmax(max-content,1fr)_auto_minmax(max-content,1fr)] items-center gap-3 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden py-1 -my-1 px-1 -mx-1">
                {/* Left: Search */}
                <div className="flex items-center justify-start min-w-0">
                    <div className="relative w-56 sm:w-64 lg:w-72 flex-shrink-0">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                            <Search size={15} />
                        </div>
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={filters.candidate_name || ''}
                            onChange={(e) => onFilterChange('candidate_name', e.target.value)}
                            placeholder="Search by candidate name, position..."
                            className="w-full pl-9 pr-10 py-1.5 text-xs sm:text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-800 placeholder-gray-400 shadow-2xs"
                        />
                        {/* Shortcut key indicator '/' or Clear button */}
                        <div className="absolute inset-y-0 right-0 pr-2 flex items-center gap-1">
                            {filters.candidate_name ? (
                                <button
                                    type="button"
                                    onClick={() => onFilterChange('candidate_name', '')}
                                    className="p-1 text-gray-400 hover:text-gray-600 rounded cursor-pointer"
                                    title="Clear search"
                                >
                                    <X size={13} />
                                </button>
                            ) : (
                                <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-[11px] font-mono text-gray-400 bg-gray-50 border border-gray-200 rounded">
                                    /
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Center: status tabs */}
                <div className="flex items-center justify-center justify-self-center">
                    <div className="inline-flex items-center p-1 bg-slate-50 border border-slate-200 rounded-xl flex-nowrap gap-1">
                        {statusTabs.map((tab) => {
                            const isActive = (filters.status || '') === tab.value;
                            const theme = STATUS_TAB_THEME[tab.value] || STATUS_TAB_THEME[''];
                            return (
                                <button
                                    key={tab.value || 'all'}
                                    type="button"
                                    onClick={() => onFilterChange('status', tab.value)}
                                    aria-pressed={isActive}
                                    className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150 select-none whitespace-nowrap cursor-pointer border ${
                                        isActive ? `${theme.active} font-semibold` : theme.idle
                                    }`}
                                >
                                    <span
                                        className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? 'bg-white' : theme.dot}`}
                                        aria-hidden="true"
                                    />
                                    <span>{tab.label}</span>
                                    <span
                                        className={`text-[11px] sm:text-xs min-w-[1.25rem] text-center px-1.5 py-0.5 rounded-md font-semibold tabular-nums transition-colors ${
                                            isActive ? theme.badgeActive : theme.badge
                                        }`}
                                    >
                                        {tab.count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Right: Export CSV & Filters */}
                <div className="flex items-center justify-end gap-2 min-w-0">
                    <button
                        type="button"
                        onClick={handleExportCSVClick}
                        disabled={isExporting || totalCount === 0}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer border ${
                            selectedCount > 0
                                ? 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100'
                                : 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                        }`}
                        title={selectedCount > 0 ? `Export ${selectedCount} selected to CSV` : 'Export CSV'}
                    >
                        {isExporting ? (
                            <CircularProgress size={13} sx={{ color: '#2563eb' }} />
                        ) : exportedCSV ? (
                            <Check size={14} className="text-emerald-600" />
                        ) : (
                            <Download size={14} className={selectedCount > 0 ? 'text-blue-600' : 'text-gray-500'} />
                        )}
                        <span>
                            {exportedCSV
                                ? t('interviewOverview.exported', 'Exported!')
                                : selectedCount > 0
                                    ? `Export CSV (${selectedCount})`
                                    : t('interviewOverview.exportCSV', 'Export CSV')
                            }
                        </span>
                    </button>

                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={() => setShowAdvancedFilters(true)}
                            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium border transition-colors shadow-2xs whitespace-nowrap cursor-pointer ${
                                activeAdvancedFilterCount > 0 || showAdvancedFilters
                                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                            }`}
                        >
                            <Filter size={14} className={activeAdvancedFilterCount > 0 ? 'text-blue-600' : 'text-gray-500'} />
                            <span>{t('interviewOverview.filters', 'Filters')}</span>
                            {activeAdvancedFilterCount > 0 && (
                                <span className="ml-0.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full">
                                    {activeAdvancedFilterCount}
                                </span>
                            )}
                        </button>

                        {activeAdvancedFilterCount > 0 && (
                            <button
                                type="button"
                                onClick={handleClearAdvancedFilters}
                                className="inline-flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors cursor-pointer shadow-2xs"
                                title={t('interviewOverview.clearFilters', 'Clear advanced filters')}
                                aria-label="Clear advanced filters"
                            >
                                <X size={15} className="stroke-[2.5]" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Advanced Filters Slide-Over Drawer matching Image 2 */}
            <Drawer
                anchor={isRTL ? 'left' : 'right'}
                open={Boolean(showAdvancedFilters)}
                onClose={() => setShowAdvancedFilters(false)}
                transitionDuration={{ enter: 350, exit: 250 }}
                SlideProps={{
                    easing: {
                        enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
                        exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                }}
                sx={{
                    zIndex: 1350,
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                        backdropFilter: 'blur(2px)',
                    },
                    '& .MuiDrawer-paper': {
                        width: { xs: '100%', sm: '440px' },
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        background: '#ffffff',
                        boxShadow: isRTL
                            ? '10px 0 40px rgba(0, 0, 0, 0.18)'
                            : '-10px 0 40px rgba(0, 0, 0, 0.18)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                }}
            >
                {/* Drawer Header with Blue Gradient */}
                <div
                    className="flex items-center justify-between px-6 py-4 shadow-sm"
                    style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
                >
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-xs flex items-center justify-center text-white shrink-0">
                            <Filter size={18} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white tracking-tight leading-tight">
                                {t('interviewOverview.advancedFilters', 'Advanced Filters')}
                            </h2>
                            <p className="text-[11px] text-blue-100">
                                {t('interviewOverview.refineResults', 'Filter and refine your results')}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowAdvancedFilters(false)}
                        className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
                        title="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Drawer Body - Scrollable Fields */}
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
                    {/* 1. Company Filter */}
                    <PremiumSelect
                        label={t('interviewOverview.company', 'Company')}
                        icon={Building2}
                        iconColor="text-blue-600"
                        value={filters.company_id || ''}
                        onChange={(val) => onFilterChange('company_id', val)}
                        options={companyOptions}
                        placeholder={t('interviewOverview.allCompanies', 'All companies')}
                        className="w-full"
                        menuWidth="w-full"
                    />

                    {/* 2. Department Filter */}
                    <PremiumSelect
                        label={t('interviewOverview.department', 'Department')}
                        icon={Users}
                        iconColor="text-indigo-600"
                        value={filters.department_id || ''}
                        onChange={(val) => onFilterChange('department_id', val)}
                        options={departmentOptions}
                        placeholder={t('interviewOverview.allDepartments', 'All departments')}
                        className="w-full"
                        menuWidth="w-full"
                    />

                    {/* 3. Company Admin Filter */}
                    <PremiumSelect
                        label={t('interviewOverview.companyAdmin', 'Company admin')}
                        icon={UserRound}
                        iconColor="text-violet-600"
                        value={filters.scheduled_by || ''}
                        onChange={(val) => onFilterChange('scheduled_by', val)}
                        options={companyAdminOptions}
                        placeholder={t('interviewOverview.allCompanyAdmins', 'All company admins')}
                        className="w-full"
                        menuWidth="w-full"
                    />

                    {/* 4. Position Filter */}
                    <PremiumSelect
                        label={t('interviewOverview.position', 'Position')}
                        icon={Briefcase}
                        iconColor="text-amber-600"
                        value={filters.job_description_id || ''}
                        onChange={(val) => onFilterChange('job_description_id', val)}
                        options={positionOptions}
                        placeholder={t('interviewOverview.allPositions', 'All positions')}
                        className="w-full"
                        menuWidth="w-full"
                    />

                    {/* 5. Interview Date Range */}
                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-1.5 select-none">
                            <Calendar size={13} className="text-blue-600" />
                            <span>{t('interviewOverview.interviewDateRange', 'Interview date range')}</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <PremiumDatePicker
                                    value={filters.filter_date_from || ''}
                                    max={filters.filter_date_to && filters.filter_date_to < todayStr ? filters.filter_date_to : todayStr}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val && val > todayStr) {
                                            onFilterChange('filter_date_from', todayStr);
                                        } else {
                                            onFilterChange('filter_date_from', val);
                                        }
                                    }}
                                    placeholder="dd-mm-yyyy"
                                />
                            </div>
                            <div>
                                <PremiumDatePicker
                                    value={filters.filter_date_to || ''}
                                    min={filters.filter_date_from || ''}
                                    max={todayStr}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val && val > todayStr) {
                                            onFilterChange('filter_date_to', todayStr);
                                        } else {
                                            onFilterChange('filter_date_to', val);
                                        }
                                    }}
                                    placeholder="dd-mm-yyyy"
                                    align="right"
                                />
                            </div>
                        </div>
                    </div>

                    {/* 6. Tags Filter with colorful chips */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 select-none">
                                <Tag size={13} className="text-violet-600" />
                                <span>{t('interviewOverview.tags', 'Tags')}</span>
                            </label>
                            {tagsLoading && (
                                <span className="text-[11px] text-gray-400">Loading tags...</span>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {allDisplayTags.map((tag) => {
                                const isSelected = selectedTags.includes(tag);
                                const color = getTagColor(tag);
                                return (
                                    <button
                                        key={tag}
                                        type="button"
                                        onClick={() => handleTagToggle(tag)}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 border cursor-pointer select-none ${
                                            isSelected
                                                ? 'bg-blue-600 text-white border-blue-600 shadow-sm ring-2 ring-blue-400/25'
                                                : `${color.bg} ${color.text} ${color.border} ${color.hover} hover:shadow-2xs`
                                        }`}
                                    >
                                        {isSelected ? (
                                            <Check size={12} className="stroke-[2.5]" />
                                        ) : (
                                            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                                        )}
                                        <span>{tag}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* 7. Show archived toggle */}
                    <div className="pt-2 border-t border-slate-100">
                        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={filters.show_archived === 'true'}
                                onChange={(e) => onFilterChange('show_archived', e.target.checked ? 'true' : '')}
                                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                            <span className="text-xs font-semibold text-slate-700">
                                {t('interviewOverview.showArchived', 'Show archived interviews')}
                            </span>
                        </label>
                    </div>
                </div>

                {/* Drawer Footer matching Image 2 */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
                    <button
                        type="button"
                        onClick={() => {
                            onClearFilters();
                            setSelectedTags([]);
                        }}
                        className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                        {t('interviewOverview.reset', 'Reset')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowAdvancedFilters(false)}
                        className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors cursor-pointer"
                    >
                        {t('interviewOverview.showResults', {
                            count: totalCount,
                            defaultValue: `Show ${totalCount} results`
                        })}
                    </button>
                </div>
            </Drawer>
        </div>
    );
};

export default React.memo(InterviewFilters);